import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Tooltip } from 'react-tooltip';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getLoanApplications, getGetLoanApplicationsQueryKey, cancelLoanApplication } from '../../../services/loanService';
import { LoanApplication } from '../../../services/schemas/loanServiceSchemas/loanApplication';
import { LoanApplicationStatus } from '../../../services/schemas/loanServiceSchemas/loanApplicationStatus';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { Dropdown } from 'react-bootstrap';
import { Modal as BootstrapModal } from 'bootstrap';
import LoanEditModal from '../../../components/modals/LoanEditModal';
import { toast } from 'react-toastify';
import './Applications.css';
import { declineReasonLabel } from '../../../constants/declineReasons';

const COMPLETED_APPLICATION_STATUSES = new Set<LoanApplicationStatus>([
    LoanApplicationStatus.VERIFICATION_COMPLETE,
    LoanApplicationStatus.AGREEMENT_READY,
    LoanApplicationStatus.ACTIVE,
    LoanApplicationStatus.COMPLETED,
]);

const REJECTED_APPLICATION_STATUSES = new Set<LoanApplicationStatus>([
    LoanApplicationStatus.VERIFICATION_REJECTED,
    LoanApplicationStatus.REJECTED_EVALUATION,
    LoanApplicationStatus.REJECTED_COLLATERAL,
    LoanApplicationStatus.REJECTED,
]);

const Applications = () => {
    const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
    const [page, setPage] = useState<number>(1);
    const [size, setSize] = useState<number>(10);
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const ALL_APPLICATIONS_PAGE_SIZE = 10000;
    const [selectedApplication, setSelectedApplication] = useState<LoanApplication | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelTarget, setCancelTarget] = useState<LoanApplication | null>(null);
    const [cancelReason, setCancelReason] = useState('');
    const [isCancelling, setIsCancelling] = useState(false);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    
    // Advanced search state
    const [searchCustomerName, setSearchCustomerName] = useState<string>('');
    const [searchStatus, setSearchStatus] = useState<string>('');
    const [currentCustomerName, setCurrentCustomerName] = useState<string | undefined>(undefined);
    const [currentSearchStatus, setCurrentSearchStatus] = useState<string | undefined>(undefined);

    // Debug: log status selections
    useEffect(() => {
        console.log('[Applications] Quick filter status:', filterStatus || 'ALL');
    }, [filterStatus]);

    useEffect(() => {
        console.log('[Applications] Advanced search status input:', searchStatus || 'ALL');
    }, [searchStatus]);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: getGetLoanApplicationsQueryKey({ 
            page: 0, 
            size: ALL_APPLICATIONS_PAGE_SIZE, 
            status: currentSearchStatus || filterStatus,
            customerName: currentCustomerName
        }),
        queryFn: () => getLoanApplications({ 
            page: 0, 
            size: ALL_APPLICATIONS_PAGE_SIZE, 
            status: currentSearchStatus || filterStatus,
            customerName: currentCustomerName
        }),
    });

    const handlePerRowsChange = (newPerPage: number) => {
        setSize(newPerPage);
        setPage(1);
        setResetPaginationToggle(prev => !prev);
    };

    const handleFilterChange = (status: string | undefined) => {
        setFilterStatus(status);
        setPage(1); // Reset to first page when filter changes
        setResetPaginationToggle(prev => !prev);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        console.log('[Applications] Submitting search with status:', searchStatus || 'ALL');
        setCurrentCustomerName(searchCustomerName.trim() || undefined);
        setCurrentSearchStatus(searchStatus || undefined);
        setFilterStatus(undefined); // Clear the quick filter when using advanced search
        setPage(1); // Reset to first page when searching
        setResetPaginationToggle(prev => !prev);
    };

    // Collect unique statuses returned from backend and compare with hardcoded enum
    const backendStatuses = useMemo(() => {
        if (!data?.content) return [];
        const set = new Set<string>();
        data.content.forEach(app => {
            if (app.status) set.add(app.status);
        });
        return Array.from(set).sort();
    }, [data]);

    useEffect(() => {
        if (backendStatuses.length === 0) return;
        console.log('[Applications] Unique statuses from backend:', backendStatuses);

        const hardcoded = Object.values(LoanApplicationStatus);
        const missingInEnum = backendStatuses.filter(s => !hardcoded.includes(s as LoanApplicationStatus));
        const unusedEnum = hardcoded.filter(s => !backendStatuses.includes(s));

        if (missingInEnum.length) {
            console.warn('[Applications] Backend returned statuses not in enum (update dropdown?):', missingInEnum);
        }
        if (unusedEnum.length) {
            console.info('[Applications] Enum contains statuses not seen in current data:', unusedEnum);
        }
    }, [backendStatuses]);

    const handleClearSearch = () => {
        setSearchCustomerName('');
        setSearchStatus('');
        setCurrentCustomerName(undefined);
        setCurrentSearchStatus(undefined);
        setPage(1);
        setResetPaginationToggle(prev => !prev);
    };

    const stats = useMemo(() => {
        const applications = data?.content || [];
        const today = new Date();

        const isSameCalendarDay = (dateValue?: string) => {
            if (!dateValue) return false;

            const parsedDate = new Date(dateValue);
            if (Number.isNaN(parsedDate.getTime())) return false;

            return (
                parsedDate.getFullYear() === today.getFullYear() &&
                parsedDate.getMonth() === today.getMonth() &&
                parsedDate.getDate() === today.getDate()
            );
        };

        const completed = applications.filter((application) =>
            application.status && COMPLETED_APPLICATION_STATUSES.has(application.status)
        ).length;

        const rejected = applications.filter((application) =>
            application.status && REJECTED_APPLICATION_STATUSES.has(application.status)
        ).length;

        // Cancelled is the business withdrawing an application, not a credit decision: counted on its own.
        const cancelled = applications.filter((application) =>
            application.status === LoanApplicationStatus.CANCELLED
        ).length;

        const processedToday = applications.filter((application) => {
            const processedDate = application.modifiedDate || application.createdDate;
            return isSameCalendarDay(processedDate);
        }).length;

        return [
            { title: 'Total Applications', value: applications.length.toString(), icon: 'fa-file-alt', color: 'primary' },
            { title: 'Completed', value: completed.toString(), icon: 'fa-check-circle', color: 'success' },
            { title: 'Rejected', value: rejected.toString(), icon: 'fa-times-circle', color: 'danger' },
            { title: 'Cancelled', value: cancelled.toString(), icon: 'fa-ban', color: 'secondary', filter: LoanApplicationStatus.CANCELLED as string },
            { title: 'Processed Today', value: processedToday.toString(), icon: 'fa-clock', color: 'info' },
        ];
    }, [data]);

    const canCancelApplication = (status?: LoanApplicationStatus) => {
        if (!status) return false;
        const nonCancellable: LoanApplicationStatus[] = [
            LoanApplicationStatus.ACTIVE,
            LoanApplicationStatus.COMPLETED,
            LoanApplicationStatus.CANCELLED,
        ];
        return !nonCancellable.includes(status);
    };

    const handleCancelApplication = async () => {
        if (!cancelTarget?.id || !cancelReason.trim()) {
            toast.error('Please provide a reason for cancellation');
            return;
        }
        console.log('Cancelling application:', cancelTarget.id, 'Reason:', cancelReason.trim());
        setIsCancelling(true);
        try {
            await cancelLoanApplication(cancelTarget.id, { reason: cancelReason.trim() });
            toast.success('Application cancelled successfully');
            setShowCancelModal(false);
            setCancelTarget(null);
            setCancelReason('');
            queryClient.invalidateQueries({ queryKey: getGetLoanApplicationsQueryKey() });
        } catch (err: any) {
            console.error('Cancel application error:', err);
            const message = err?.response?.data?.message || err?.message || 'Failed to cancel application';
            toast.error(message);
        } finally {
            setIsCancelling(false);
        }
    };

    const columns = [
        {
            name: 'Application ID',
            selector: (row: LoanApplication) => row.applicationNumber || '',
            sortable: true,
        },
        {
            name: 'Customer Name',
            selector: (row: LoanApplication) => {
                if (row.individual) {
                    return row.individual.fullNames || '';
                } else if (row.company) {
                    return row.company.companyName || '';
                }
                return '';
            },
            sortable: true,
        },
        {
            name: 'Type',
            selector: (row: LoanApplication) => row.loanType || '',
            sortable: true,
            cell: (row: LoanApplication) => (
                <span className={`badge bg-${row.individual ? 'info' : 'primary'}`}>
                    {row.individual ? 'Individual' : 'Company'}
                </span>
            ),
        },
        {
            name: 'Amount',
            selector: (row: LoanApplication) => row.loanDetails?.loanAmount || 0,
            sortable: true,
            format: (row: LoanApplication) => formatCurrency(row.loanDetails?.loanAmount || 0),
        },
        {
            name: 'Status',
            selector: (row: LoanApplication) => row.status || '',
            sortable: true,
            cell: (row: LoanApplication) => {
                const statusColor = getStatusColor(row.status);
                const isPending = row.status === LoanApplicationStatus.PENDING_SUBMISSION || 
                                 row.status === LoanApplicationStatus.SUBMITTED ||
                                 row.status === LoanApplicationStatus.VERIFICATION_IN_PROGRESS;
                return (
                    <div>
                        <span className={`badge bg-${statusColor} badge-enhanced ${isPending ? 'status-badge-pending' : ''}`}>
                            {row.status?.replace(/_/g, ' ')}
                        </span>
                        {/* Why and by whom it was cancelled: the application is kept, so this is its record */}
                        {row.status === LoanApplicationStatus.CANCELLED && (row.cancellationReason || row.cancelledBy) && (
                            <div className="small text-muted mt-1" style={{ maxWidth: 220 }} title={row.cancellationReason || undefined}>
                                {row.cancellationReason && <div className="text-truncate">{row.cancellationReason}</div>}
                                {(row.cancelledBy || row.cancelledAt) && (
                                    <div>{[row.cancelledBy, row.cancelledAt ? new Date(row.cancelledAt).toLocaleDateString('en-ZA') : null].filter(Boolean).join(' · ')}</div>
                                )}
                            </div>
                        )}
                        {/* Why it was declined, next to the declined status */}
                        {row.declineReasonCategory && (
                            <div className="small text-danger mt-1" title={row.declineReason || undefined}>
                                {declineReasonLabel(row.declineReasonCategory)}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            name: 'Submission Date',
            selector: (row: LoanApplication) => row.createdDate || '',
            sortable: true,
            format: (row: LoanApplication) => formatDate(row.createdDate),
        },
        {
            name: 'Actions',
            cell: (row: LoanApplication) => (
                <Dropdown align="end">
                    <Dropdown.Toggle variant="light" size="sm" className="rounded-pill d-flex align-items-center no-caret">
                        <span>Actions</span>
                        <i className="fa fa-caret-down ms-2"></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => navigate(`/application/${row.id}`)}>
                            <i className="fa fa-eye me-2 text-primary"></i>
                            View Details
                        </Dropdown.Item>
                        {row.loanType === 'PERSONAL' && (
                            <Dropdown.Item onClick={() => navigate(`/loan-application-documents/view/${encodeURIComponent(row.id || '')}`)}>
                                <i className="fa fa-folder-open me-2 text-info"></i>
                                View Documents
                            </Dropdown.Item>
                        )}
                        {row.status !== LoanApplicationStatus.CANCELLED && (
                        <Dropdown.Item onClick={() => {
                            setSelectedApplication(row);
                            setShowEditModal(true);
                            setTimeout(() => {
                                const modalElement = document.getElementById('LoanEditModal');
                                if (modalElement) {
                                    const modal = new BootstrapModal(modalElement);
                                    modal.show();
                                }
                            }, 100);
                        }}>
                            <i className="fa fa-pencil me-2 text-success"></i>
                            Update Application
                        </Dropdown.Item>
                        )}
                        {row.status === LoanApplicationStatus.DOCUMENT_COLLECTION && (
                            <Dropdown.Item onClick={() => navigate(`/loan-application-documents/${row.id}`)}>
                                <i className="fa fa-upload me-2 text-info"></i>
                                Upload Documents
                            </Dropdown.Item>
                        )}
                        {canCancelApplication(row.status) && (
                            <Dropdown.Item onClick={() => {
                                setCancelTarget(row);
                                setCancelReason('');
                                setShowCancelModal(true);
                            }}>
                                <i className="fa fa-times-circle me-2 text-danger"></i>
                                Cancel Application
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            ),
            // The menu opens on its own; without this a click inside it would also open the row.
            ignoreRowClick: true,
        },
    ];

    // Helper function to determine status color
    const getStatusColor = (status?: LoanApplicationStatus) => {
        switch (status) {
            case LoanApplicationStatus.VERIFICATION_COMPLETE:
            case LoanApplicationStatus.AGREEMENT_READY:
            case LoanApplicationStatus.ACTIVE:
            case LoanApplicationStatus.COMPLETED:
                return 'success';
            case LoanApplicationStatus.REJECTED:
            case LoanApplicationStatus.REJECTED_EVALUATION:
            case LoanApplicationStatus.REJECTED_COLLATERAL:
            case LoanApplicationStatus.CANCELLED:
                return 'danger';
            case LoanApplicationStatus.PENDING_SUBMISSION:
            case LoanApplicationStatus.SUBMITTED:
            case LoanApplicationStatus.DOCUMENT_COLLECTION:
                return 'warning';
            case LoanApplicationStatus.PENDING_DISBURSEMENT:
            case LoanApplicationStatus.VERIFICATION_IN_PROGRESS:
            case LoanApplicationStatus.EVALUATION_IN_PROGRESS:
            case LoanApplicationStatus.AGREEMENT_PREPARATION:
            case LoanApplicationStatus.AGREEMENT_UPLOAD:
            case LoanApplicationStatus.COLLATERAL_VERIFICATION:
            case LoanApplicationStatus.DISBURSEMENT_IN_PROGRESS:
                return 'info';
            default:
                return 'secondary';
        }
    };

    return (
        <div className="applications-page">
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Applications</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    {/* Stats Cards */}
                    <div className="row g-3 mb-4 row-deck">
                        {stats.map((stat: { title: string; value: string; icon: string; color: string; filter?: string }, index) => (
                            <div key={index} className="col-xl col-md-4 col-sm-6">
                                <div className="card stats-card-enhanced h-100"
                                    {...(stat.filter ? {
                                        role: 'button',
                                        title: filterStatus === stat.filter ? 'Show all applications' : `Show ${stat.title.toLowerCase()} applications`,
                                        onClick: () => handleFilterChange(filterStatus === stat.filter ? undefined : stat.filter),
                                        style: filterStatus === stat.filter ? { outline: '2px solid #198754' } : undefined,
                                    } : {})}>
                                    <div className="card-body p-4">
                                        <i className={`fa ${stat.icon} stat-icon position-absolute top-0 end-0 mt-4 me-3 text-${stat.color}`}></i>
                                        <div className="mb-2 text-uppercase small text-muted fw-semibold">{stat.title}</div>
                                        <div><span className="h4 stat-value mb-0">{stat.value}</span></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Advanced Search Card */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card search-card-enhanced">
                                <div className="card-header bg-transparent border-0 pb-2">
                                    <h6 className="card-title mb-0 d-flex align-items-center">
                                        <i className="fa fa-search me-2 text-primary"></i>Advanced Search
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleSearch}>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <label htmlFor="customerName" className="form-label fw-semibold">Customer Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="customerName"
                                                    placeholder="Enter customer name..."
                                                    value={searchCustomerName}
                                                    onChange={(e) => setSearchCustomerName(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label htmlFor="status" className="form-label fw-semibold">Status</label>
                                                <select
                                                    className="form-select"
                                                    id="status"
                                                    value={searchStatus}
                                                    onChange={(e) => setSearchStatus(e.target.value)}
                                                >
                                                    <option value="">All Statuses</option>
                                                    {Object.values(LoanApplicationStatus).map((status) => (
                                                        <option key={status} value={status}>
                                                            {status.replace(/_/g, ' ')}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-4 d-flex align-items-end">
                                                <div className="d-flex gap-2 w-100">
                                                    <button type="submit" className="btn btn-primary btn-enhanced flex-fill">
                                                        <i className="fa fa-search me-1"></i>Search
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-enhanced"
                                                        onClick={handleClearSearch}
                                                    >
                                                        <i className="fa fa-times me-1"></i>Clear
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        {(currentCustomerName || currentSearchStatus) && (
                                            <div className="mt-3 pt-3 border-top">
                                                <small className="text-muted d-flex align-items-center flex-wrap gap-2">
                                                    <i className="fa fa-info-circle"></i>
                                                    <span className="fw-semibold">Active filters:</span>
                                                    {currentCustomerName && (
                                                        <span className="badge bg-info badge-enhanced filter-badge">
                                                            Customer: {currentCustomerName}
                                                        </span>
                                                    )}
                                                    {currentSearchStatus && (
                                                        <span className="badge bg-info badge-enhanced filter-badge">
                                                            Status: {currentSearchStatus.replace(/_/g, ' ')}
                                                        </span>
                                                    )}
                                                </small>
                                            </div>
                                        )}
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card table-card-enhanced">
                                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <h6 className="card-title mb-0 flex-grow-1 d-flex align-items-center">
                                        <i className="fa fa-file-alt me-2 text-primary"></i>
                                        Applications
                                    </h6>
                                    <Link
                                        to="/pending-submission"
                                        className="btn btn-outline-secondary btn-sm"
                                        data-tooltip-id="pending-submissions"
                                    >
                                        <i className="fa fa-pencil-square-o me-2"></i>
                                        Pending Submissions
                                    </Link>
                                    <Tooltip id="pending-submissions" place="top" content="Applications still being captured, saved as drafts" />
                                    <Link 
                                        to="/loan-application/new" 
                                        className="btn btn-success btn-sm btn-enhanced"
                                        data-tooltip-id="new-application"
                                    >
                                        <i className="fa fa-plus me-2"></i>
                                        New Application
                                    </Link>
                                    <Tooltip id="new-application" place="top" content="Start new application" />
                                </div>
                                <div className="card-body p-0">
                                    {isLoading ? (
                                        <div className="text-center loading-container-enhanced">
                                            <div className="loading-spinner-enhanced" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="loading-text-enhanced mb-0">Loading applications...</p>
                                        </div>
                                    ) : isError ? (
                                        <div className="alert alert-danger error-container-enhanced m-4">
                                            <i className="fa fa-exclamation-triangle me-2"></i>
                                            Error loading applications: {(error as Error)?.message || 'Unknown error'}
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={data?.content || []}
                                                pagination
                                                paginationDefaultPage={page}
                                                paginationResetDefaultPage={resetPaginationToggle}
                                                onChangePage={(nextPage) => setPage(nextPage)}
                                                onChangeRowsPerPage={handlePerRowsChange}
                                                paginationPerPage={size}
                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                striped
                                                responsive
                                                progressPending={isLoading}
                                                onRowClicked={(row: LoanApplication) => row.id && navigate(`/application/${row.id}`)}
                                                pointerOnHover
                                                highlightOnHover
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Update Loan Modal */}
            {showEditModal && selectedApplication && (
                <div className="modal fade" id="LoanEditModal" tabIndex={-1} aria-hidden="true">
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <LoanEditModal
                            applicationId={selectedApplication.id || ''}
                            loan={{
                                loanAmount: selectedApplication.loanDetails?.loanAmount,
                                interestRate: selectedApplication.loanDetails?.interestRate,
                                dueDate: selectedApplication.loanDetails?.dueDate,
                                commencementDate: selectedApplication.loanDetails?.commencementDate,
                                adminFee: selectedApplication.loanDetails?.adminFee,
                                customerName: selectedApplication.individual?.fullNames || selectedApplication.company?.companyName,
                                accountNumber: selectedApplication.applicationNumber,
                            }}
                            onSuccess={() => {
                                queryClient.invalidateQueries({ queryKey: getGetLoanApplicationsQueryKey({
                                    page: 0,
                                    size: ALL_APPLICATIONS_PAGE_SIZE,
                                    status: currentSearchStatus || filterStatus,
                                    customerName: currentCustomerName
                                }) });
                                setShowEditModal(false);
                                setSelectedApplication(null);
                            }}
                            onClose={() => {
                                setShowEditModal(false);
                                setSelectedApplication(null);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Cancel Application Modal */}
            {showCancelModal && cancelTarget && (
                <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                            <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.3)' }}>
                                        <i className="fa fa-times-circle text-danger" style={{ display: 'block' }}></i>
                                    </div>
                                    <div>
                                        <h5 className="modal-title mb-0 fw-bold">Cancel Application</h5>
                                        <p className="text-muted mb-0 small">This action cannot be undone</p>
                                    </div>
                                </div>
                                <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)} disabled={isCancelling}></button>
                            </div>
                            <div className="modal-body p-4">
                                <p className="text-muted mb-2">
                                    Cancelling application: <strong>{cancelTarget.applicationNumber || cancelTarget.id}</strong>
                                </p>
                                <p className="text-muted mb-3">Please provide a reason for cancellation:</p>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    placeholder="Enter reason for cancellation..."
                                    disabled={isCancelling}
                                    maxLength={500}
                                />
                                <small className="text-muted">{cancelReason.length}/500 characters</small>
                            </div>
                            <div className="modal-footer border-0 px-4 pb-4">
                                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger rounded-pill px-4"
                                    onClick={handleCancelApplication}
                                    disabled={isCancelling || !cancelReason.trim()}
                                >
                                    {isCancelling ? (
                                        <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Cancelling...</>
                                    ) : (
                                        <><i className="fa fa-times-circle me-2"></i>Cancel Application</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Applications;
